/**
 * Reports & compliance → Scheduled reports.
 *
 * WHY THIS SCREEN EXISTS
 * Report schedules could only point at a saved report from the builder. Every
 * built-in is one of those, so query-shaped reports were covered — but the
 * documents an auditor actually asks for were not. The compliance framework
 * report, evidence pack, QBR, plain-English assessment and inventory PDF each
 * have their own generator behind their own button, and the only way to get
 * one was for somebody to remember to click it.
 *
 * "Send me the HIPAA posture on the first of every month" is the entire shape
 * of a compliance engagement, and it could not be expressed.
 *
 * The form is driven by /reports-builder/schedulable rather than a hardcoded
 * list: which reports exist, which need a customer, which formats each renders,
 * and the framework choices all come from the server. A dropdown here cannot
 * drift from what the builders accept.
 *
 * TWO LATER FIXES, both reported:
 *
 *  - "any / all reports" — the picker offered 11 of 30 runnable reports,
 *    because only the generated DOCUMENTS were schedulable. The other 19 could
 *    be scheduled solely by saving a copy first. /schedulable returns both, and
 *    a standard report is stored as report_type_key rather than document_key.
 *
 *  - "the hour in UTC instead of respecting the timezone set under settings" —
 *    the field asked for a UTC hour, so 9am Eastern meant typing 13, and being
 *    an hour out for half the year. The schedule now carries its zone and the
 *    server resolves the hour in it, so 9am stays 9am across daylight saving.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import ActionButton from "../components/ActionButton";
import { formatDateTime } from "../utils/datetime";
import { useBranding } from "../context/BrandingContext";

type Choice = { value: string | number; label: string };
type ParamSpec = { label: string; choices?: Choice[]; required?: boolean };
/** Anything schedulable: a generated document or a standard report type. */
type DocReport = {
  kind?: "document" | "report_type";
  key: string;
  label: string;
  description: string;
  category?: string;
  needs_customer: boolean;
  formats: string[];
  params: Record<string, ParamSpec>;
};
type Schedule = {
  id: number;
  saved_report_id: number | null;
  document_key: string | null;
  document_label: string | null;
  document_params: Record<string, any> | null;
  cadence: string;
  hour: number;
  timezone: string | null;
  report_type_key: string | null;
  day_of_week: number | null;
  day_of_month: number | null;
  recipients: string[];
  output_format: string;
  enabled: boolean;
  last_run_at: string | null;
  last_run_status: string | null;
  last_run_error: string | null;
  next_run_at: string | null;
};

const DAYS = ["", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function ScheduledDocuments() {
  // The zone configured under Settings, not the browser's and not UTC. The
  // whole point of the report was that this screen ignored it.
  const { display_timezone: tz } = useBranding();
  const [docs, setDocs] = useState<DocReport[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly: this payload goes
  // into a model prompt.
  usePublishScreenData(
    "Scheduled reports",
    useMemo(
      () =>
        schedules.map((s: any) => ({
          document: s.document_label ?? s.document_key ?? s.report_type_key ?? null,
          cadence: s.cadence,
          hour: s.hour,
          timezone: s.timezone ?? null,
          enabled: s.enabled,
          recipients: Array.isArray(s.recipients) ? s.recipients.length : null,
          last_run_at: s.last_run_at ?? null,
          last_run_status: s.last_run_status ?? null,
          // A COUNT of recipients, not the addresses. Those are somebody's
          // contact details and the assistant does not need them to answer
          // "which of these is failing".
        })),
      [schedules],
    ),
    `${docs.length} document type(s) available`,
  );
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [docKey, setDocKey] = useState("");
  const [params, setParams] = useState<Record<string, any>>({});
  const [format, setFormat] = useState("pdf");
  const [cadence, setCadence] = useState("monthly");
  const [hour, setHour] = useState(9);
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [recipients, setRecipients] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        api.get("/api/v1/reports-builder/schedulable"),
        api.get("/api/v1/reports-builder/document-schedules"),
      ]);
      setDocs(d.data || []);
      setSchedules(s.data || []);
      setError("");
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not load scheduled documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(() => docs.find((d) => d.key === docKey), [docs, docKey]);

  /** Everything schedulable, grouped for the picker. */
  const byCategory = useMemo(() => {
    const m = new Map<string, DocReport[]>();
    docs.forEach((d) => {
      const k = d.category || "Documents";
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(d);
    });
    return Array.from(m.entries());
  }, [docs]);

  // Reset the parameters when the document changes — carrying a framework over
  // to a QBR would silently send something nobody asked for.
  const chooseDoc = (key: string) => {
    setDocKey(key);
    setParams({});
    const d = docs.find((x) => x.key === key);
    setFormat(d?.formats?.[0] || "pdf");
  };

  const create = async () => {
    setError("");
    if (!docKey) {
      setError("Choose a report.");
      return;
    }
    const list = recipients
      .split(/[,;\s]+/)
      .map((r) => r.trim())
      .filter(Boolean);
    if (!list.length) {
      setError("Add at least one recipient.");
      return;
    }
    try {
      await api.post("/api/v1/reports-builder/document-schedules", {
        // A standard report type and a generated document are different
        // targets server-side; the screen treats them the same.
        ...(selected?.kind === "report_type"
          ? { report_type_key: docKey }
          : { document_key: docKey }),
        params,
        cadence,
        hour,
        timezone: tz || null,
        day_of_week: cadence === "weekly" ? dayOfWeek : null,
        day_of_month: cadence === "monthly" ? dayOfMonth : null,
        recipients: list,
        output_format: format,
        enabled: true,
      });
      setNotice(`Scheduled. ${selected?.label} will go to ${list.join(", ")}.`);
      setRecipients("");
      setDocKey("");
      setParams({});
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not create the schedule");
    }
  };

  const runNow = async (s: Schedule) => {
    setError("");
    try {
      const r = await api.post(`/api/v1/reports-builder/document-schedules/${s.id}/run`);
      setNotice(
        `Sent now to ${(r.data?.recipients || []).join(", ")}. The recurring send is ` +
          `unaffected — this did not consume it.`,
      );
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Send failed");
    }
  };

  const remove = async (s: Schedule) => {
    if (
      !window.confirm(
        `Stop sending "${s.document_label}" to ${s.recipients.join(", ")}?\n\n` +
          `Nothing else is deleted — the report itself is still available on demand.`,
      )
    )
      return;
    setError("");
    try {
      await api.delete(`/api/v1/reports-builder/schedules/${s.id}`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not delete the schedule");
    }
  };

  const describe = (s: Schedule) => {
    // Say the zone the schedule is actually in, not the one this browser
    // happens to be in and not UTC.
    const zone = s.timezone || "UTC";
    const at = `${String(s.hour).padStart(2, "0")}:00 ${zone}`;
    if (s.cadence === "daily") return `Daily at ${at}`;
    if (s.cadence === "weekly") return `Every ${DAYS[s.day_of_week || 1]} at ${at}`;
    return `Day ${s.day_of_month || 1} of each month at ${at}`;
  };

  const paramSummary = (s: Schedule) => {
    const p = s.document_params || {};
    const bits: string[] = [];
    if (p.framework) bits.push(String(p.framework).toUpperCase());
    if (p.period) bits.push(String(p.period));
    if (p.quarter) bits.push(String(p.quarter));
    const doc = docs.find((d) => d.key === s.document_key);
    const cust = doc?.params?.customer_id?.choices?.find(
      (c) => String(c.value) === String(p.customer_id),
    );
    bits.push(cust ? cust.label : p.customer_id ? `customer ${p.customer_id}` : "All customers");
    return bits.join(" · ");
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">
        Scheduled reports
      </h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-3xl">
        Have any report emailed to anyone on a recurring cadence — a standard report, an AI
        summary, or a generated document like a compliance framework report, evidence pack or
        QBR. Recipients do not need an AeroGauge login, so these can go straight to auditors and
        customer contacts.
      </p>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg px-3 py-2">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300 text-sm rounded-lg px-3 py-2 flex items-start justify-between gap-3">
          <span>{notice}</span>
          <button onClick={() => setNotice("")} className="text-xs shrink-0">
            ✕
          </button>
        </div>
      )}

      {loading && <p className="text-sm text-gray-400">Loading…</p>}

      {/* New schedule */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
          New scheduled document
        </h2>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="lg:col-span-3">
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Report or document
            </label>
            <select
              value={docKey}
              onChange={(e) => chooseDoc(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Choose…</option>
              {/* Grouped: thirty flat options is a list to read, not a menu. */}
              {byCategory.map(([cat, items]) => (
                <optgroup key={cat} label={cat}>
                  {items.map((d) => (
                    <option key={d.key} value={d.key}>
                      {d.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {selected && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                {selected.description}
              </p>
            )}
          </div>

          {/* Parameters, driven entirely by the server's registry. */}
          {selected &&
            Object.entries(selected.params).map(([name, spec]) => (
              <div key={name}>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {spec.label}
                  {spec.required && <span className="text-red-500"> *</span>}
                </label>
                {spec.choices && spec.choices.length ? (
                  <select
                    value={params[name] ?? ""}
                    onChange={(e) =>
                      setParams((p) => ({ ...p, [name]: e.target.value || undefined }))
                    }
                    className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">
                      {spec.required ? "Choose…" : "All customers"}
                    </option>
                    {spec.choices.map((c) => (
                      <option key={String(c.value)} value={String(c.value)}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={params[name] ?? ""}
                    onChange={(e) =>
                      setParams((p) => ({ ...p, [name]: e.target.value || undefined }))
                    }
                    placeholder="blank = most recent closed period"
                    className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                  />
                )}
              </div>
            ))}

          {selected && (
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Format</label>
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
              >
                {selected.formats.map((f) => (
                  <option key={f} value={f}>
                    {f.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Cadence</label>
            <select
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          {cadence === "weekly" && (
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Day</label>
              <select
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(Number(e.target.value))}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
              >
                {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                  <option key={d} value={d}>
                    {DAYS[d]}
                  </option>
                ))}
              </select>
            </div>
          )}

          {cadence === "monthly" && (
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Day of month
              </label>
              <input
                type="number"
                min={1}
                max={28}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Number(e.target.value))}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
              />
              {/* 28 is the cap on purpose: a "31st" schedule would skip four
                  months a year, silently. */}
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                Capped at 28 so it fires every month, February included.
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Hour
            </label>
            <input
              type="number"
              min={0}
              max={23}
              value={hour}
              onChange={(e) => setHour(Number(e.target.value))}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
            />
            {/* Naming the zone matters: the previous version asked for a UTC
                hour without saying so on the field itself. */}
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
              {String(hour).padStart(2, "0")}:00 {tz || "UTC"} — the zone set under
              Settings. Stays correct across daylight saving.
            </p>
          </div>

          <div className="lg:col-span-2">
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Recipients
            </label>
            <input
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="auditor@example.com, cfo@customer.com"
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="mt-4">
          <ActionButton
            onAction={create}
            className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700"
            pendingLabel="Scheduling…"
          >
            Create schedule
          </ActionButton>
        </div>
      </div>

      {/* Existing */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {schedules.length} scheduled report{schedules.length === 1 ? "" : "s"}
          </h2>
        </div>
        {schedules.length === 0 ? (
          <p className="px-5 py-6 text-sm text-gray-500 dark:text-gray-400">
            Nothing scheduled yet.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500 dark:text-gray-400">
              <tr>
                <th className="text-left px-5 py-2 font-medium">Document</th>
                <th className="text-left px-3 py-2 font-medium">Cadence</th>
                <th className="text-left px-3 py-2 font-medium">Recipients</th>
                <th className="text-left px-3 py-2 font-medium">Last run</th>
                <th className="text-left px-3 py-2 font-medium">Next</th>
                <th className="text-right px-5 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {schedules.map((s) => (
                <tr key={s.id}>
                  <td className="px-5 py-2">
                    <div className="text-gray-900 dark:text-gray-100">{s.document_label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {paramSummary(s)} · {s.output_format.toUpperCase()}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">
                    {describe(s)}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">
                    {s.recipients.join(", ")}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {!s.last_run_at ? (
                      <span className="text-gray-400 dark:text-gray-500">never</span>
                    ) : s.last_run_status === "success" ? (
                      <span className="text-green-700 dark:text-green-400">
                        {formatDateTime(s.last_run_at, tz)}
                      </span>
                    ) : (
                      <span
                        className="text-red-700 dark:text-red-400"
                        title={s.last_run_error || ""}
                      >
                        failed {formatDateTime(s.last_run_at, tz)}
                      </span>
                    )}
                    {s.last_run_status !== "success" && s.last_run_error && (
                      <div className="text-[11px] text-red-600 dark:text-red-400 max-w-xs truncate">
                        {s.last_run_error}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {s.next_run_at ? formatDateTime(s.next_run_at, tz) : "—"}
                  </td>
                  <td className="px-5 py-2 text-right whitespace-nowrap">
                    {/* A recurring report nobody can test is one nobody trusts,
                        and these take parameters that are easy to get subtly
                        wrong in a way you find out about a month later. */}
                    <ActionButton
                      onAction={() => runNow(s)}
                      className="text-xs px-2.5 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 mr-2"
                      pendingLabel="Sending…"
                    >
                      Send now
                    </ActionButton>
                    <ActionButton
                      onAction={() => remove(s)}
                      className="text-xs px-2.5 py-1 rounded border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                      pendingLabel="Removing…"
                    >
                      Delete
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
